import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Employee Impact brochure — Phase 6 sales surface for engagement + performance.
 *
 * Tone: "the signals that predict whether people stay and grow". Pitch lands
 * on the engagement lead who watched eNPS slip 8 points last quarter and the
 * manager juggling six reports and two 1:1s a week.
 *
 * Illustration: a small dashboard slice — an eNPS gauge arc, a heart-shaped
 * recognition pill, a goal-progress ring. One floating accent pill labeled
 * "+12 NPS" anchors the eye to the outcome.
 *
 * Mockup: a faux engagement dashboard. Left: an eNPS line chart over four
 * quarters drawn as an inline SVG polyline. Right: a "Top recognized this
 * month" mini list with three avatars and value tags.
 */

// ----- Illustration: dashboard slice — eNPS gauge + heart pill + goal ring -----

const ImpactIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  // Gauge geometry. We draw a half-arc and a small needle ~70% along it.
  const gaugeRadius = 56;
  const cx = 80;
  const cy = 80;
  // Arc from 180° → 0° (left → right, top half).
  const arcStart = `M ${cx - gaugeRadius} ${cy}`;
  const arcEnd = `A ${gaugeRadius} ${gaugeRadius} 0 0 1 ${cx + gaugeRadius} ${cy}`;
  // Needle at 70% — angle = 180 - 0.7*180 = 54°.
  const needleAngleDeg = 180 - 0.7 * 180;
  const needleAngleRad = (needleAngleDeg * Math.PI) / 180;
  const needleX = cx + Math.cos(needleAngleRad) * (gaugeRadius - 6);
  const needleY = cy - Math.sin(needleAngleRad) * (gaugeRadius - 6);

  // Goal ring — circumference math for a stroke-dasharray progress.
  const ringRadius = 26;
  const ringCircum = 2 * Math.PI * ringRadius;
  const ringProgress = 0.68;

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Atmospheric SVG — soft rose wash + faint grid. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <pattern id="imp-grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path
              d="M 28 0 L 0 0 0 28"
              fill="none"
              stroke="rgb(244 63 94 / 0.07)"
              strokeWidth="1"
            />
          </pattern>
          <radialGradient id="imp-glow" cx="50%" cy="55%" r="55%">
            <stop offset="50%" stopColor="rgb(244 63 94 / 0)" />
            <stop offset="100%" stopColor="rgb(244 63 94 / 0.16)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#imp-grid)" />
        <ellipse cx="160" cy="220" rx="150" ry="140" fill="url(#imp-glow)" />
      </svg>

      {/* Dashboard card */}
      <div className="absolute inset-0 m-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">
            {t("brochure.impact.illustration.heading")}
          </p>
          <span className="inline-flex h-5 items-center gap-1 rounded-md bg-slate-50 px-1.5 text-[9px] font-medium text-slate-500 ring-1 ring-slate-200">
            <Icon name="Calendar" size={9} aria-hidden="true" />
            {t("brochure.impact.illustration.q")}
          </span>
        </div>

        {/* eNPS gauge */}
        <div className="mt-3 rounded-xl bg-slate-50/70 p-3">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">
            {t("brochure.impact.illustration.eNps")}
          </p>
          <div className="relative mx-auto mt-1 h-[88px] w-[160px]">
            <svg
              viewBox="0 0 160 90"
              className="absolute inset-0 h-full w-full"
              aria-hidden="true"
            >
              {/* Track */}
              <path
                d={`${arcStart} ${arcEnd}`}
                fill="none"
                stroke="rgb(226 232 240)"
                strokeWidth="8"
                strokeLinecap="round"
              />
              {/* Active arc — 0% → 70%. */}
              <path
                d={`${arcStart} ${arcEnd}`}
                fill="none"
                stroke="rgb(244 63 94)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${Math.PI * gaugeRadius * 0.7} ${Math.PI * gaugeRadius}`}
              />
              {/* Needle */}
              <line
                x1={cx}
                y1={cy}
                x2={needleX}
                y2={needleY}
                stroke="rgb(15 23 42)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx={cx} cy={cy} r="3" fill="rgb(15 23 42)" />
            </svg>
            <div className="absolute inset-x-0 bottom-1 text-center">
              <p className="text-xl font-semibold tabular-nums leading-none text-slate-900">
                +42
              </p>
              <p className="text-[8px] font-medium text-slate-500">
                {t("brochure.impact.illustration.score")}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom row — recognition pill + goal ring */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* Recognition pill */}
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-2.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-100 text-rose-700">
              <Icon name="Heart" size={14} aria-hidden="true" />
            </span>
            <p className="mt-1.5 text-[8px] font-semibold uppercase tracking-wider text-rose-700">
              {t("brochure.impact.illustration.recognition")}
            </p>
            <p className="mt-0.5 text-base font-semibold tabular-nums text-rose-900">
              328
            </p>
            <p className="text-[8px] text-rose-800/70">
              {t("brochure.impact.illustration.shoutouts")}
            </p>
          </div>

          {/* Goal ring */}
          <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-2.5">
            <div className="relative h-16 w-16">
              <svg
                viewBox="0 0 72 72"
                className="absolute inset-0 h-full w-full -rotate-90"
                aria-hidden="true"
              >
                <circle
                  cx="36"
                  cy="36"
                  r={ringRadius}
                  fill="none"
                  stroke="rgb(226 232 240)"
                  strokeWidth="8"
                />
                <circle
                  cx="36"
                  cy="36"
                  r={ringRadius}
                  fill="none"
                  stroke="rgb(244 63 94)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${ringCircum * ringProgress} ${ringCircum}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-sm font-semibold tabular-nums text-slate-900">
                  68%
                </span>
              </div>
            </div>
            <p className="mt-1 text-[8px] font-semibold uppercase tracking-wider text-slate-500">
              {t("brochure.impact.illustration.goals")}
            </p>
          </div>
        </div>
      </div>

      {/* Floating accent pill — "+12 NPS". */}
      <div
        className={cn(
          "absolute top-[8%] flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1.5 text-[10px] font-semibold text-white shadow-[0_14px_30px_-8px_rgba(225,29,72,0.55)] ring-2 ring-white",
          isRtl ? "left-[6%]" : "right-[6%]",
        )}
      >
        <Icon name="TrendingUp" size={12} aria-hidden="true" />
        <span>{t("brochure.impact.illustration.npsLift")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: engagement dashboard — eNPS line chart + top recognized list -----

interface RecognizedPerson {
  initials: string;
  tint: string;
  nameKey: string;
  valueKey: string;
}

const RECOGNIZED: RecognizedPerson[] = [
  {
    initials: "AY",
    tint: "bg-rose-100 text-rose-700",
    nameKey: "brochure.impact.mockup.recognized.aisha",
    valueKey: "brochure.impact.mockup.value.craft",
  },
  {
    initials: "TM",
    tint: "bg-amber-100 text-amber-800",
    nameKey: "brochure.impact.mockup.recognized.tariq",
    valueKey: "brochure.impact.mockup.value.ownership",
  },
  {
    initials: "FB",
    tint: "bg-emerald-100 text-emerald-700",
    nameKey: "brochure.impact.mockup.recognized.fatima",
    valueKey: "brochure.impact.mockup.value.kindness",
  },
];

const ImpactMockup: React.FC = () => {
  const { t } = useTranslation();

  // 4-quarter eNPS series. Pick scores that climb modestly so the polyline
  // tells the same "we lifted engagement" story the brochure pitches.
  const series = [
    { qKey: "brochure.impact.mockup.q.q1", value: 24 },
    { qKey: "brochure.impact.mockup.q.q2", value: 31 },
    { qKey: "brochure.impact.mockup.q.q3", value: 28 },
    { qKey: "brochure.impact.mockup.q.q4", value: 42 },
  ];

  // Plot the polyline onto a 320×100 viewbox, mapping value 0..60 → 90..10.
  const minV = 0;
  const maxV = 60;
  const w = 320;
  const h = 100;
  const padX = 16;
  const stepX = (w - padX * 2) / (series.length - 1);
  const points = series
    .map((p, i) => {
      const x = padX + i * stepX;
      const y = h - 10 - ((p.value - minV) / (maxV - minV)) * (h - 20);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* eNPS chart card */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="LineChart" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.impact.mockup.chart.heading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.impact.mockup.chart.range")}
          </p>
        </div>

        <div className="bg-white p-4">
          <div className="flex items-baseline gap-3">
            <p className="text-3xl font-semibold tabular-nums text-slate-900">+42</p>
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
              <Icon name="TrendingUp" size={10} aria-hidden="true" />
              {t("brochure.impact.mockup.chart.deltaYoY")}
            </span>
          </div>
          <p className="mt-0.5 text-[10px] text-slate-500">
            {t("brochure.impact.mockup.chart.subhead")}
          </p>

          {/* Inline SVG line chart */}
          <div className="mt-3">
            <svg
              viewBox={`0 0 ${w} ${h}`}
              className="h-28 w-full"
              role="img"
              aria-label={t("brochure.impact.mockup.chart.aria")}
            >
              {/* Gridlines — three horizontal hairlines. */}
              {[0.25, 0.5, 0.75].map((p) => (
                <line
                  key={p}
                  x1={padX}
                  x2={w - padX}
                  y1={h - 10 - p * (h - 20)}
                  y2={h - 10 - p * (h - 20)}
                  stroke="rgb(241 245 249)"
                  strokeWidth="1"
                />
              ))}
              {/* Area under the line — subtle wash. */}
              <polygon
                points={`${padX},${h - 10} ${points} ${w - padX},${h - 10}`}
                fill="rgb(244 63 94 / 0.08)"
              />
              {/* Polyline */}
              <polyline
                points={points}
                fill="none"
                stroke="rgb(225 29 72)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Markers + most-recent dot is larger. */}
              {series.map((p, i) => {
                const x = padX + i * stepX;
                const y = h - 10 - ((p.value - minV) / (maxV - minV)) * (h - 20);
                const isLast = i === series.length - 1;
                return (
                  <g key={i}>
                    <circle
                      cx={x}
                      cy={y}
                      r={isLast ? 4 : 3}
                      fill={isLast ? "rgb(225 29 72)" : "white"}
                      stroke="rgb(225 29 72)"
                      strokeWidth="2"
                    />
                  </g>
                );
              })}
            </svg>
            <div className="mt-1.5 flex justify-between px-3 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              {series.map((p, i) => (
                <span key={i}>{t(p.qKey)}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top recognized this month */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.impact.mockup.recognized.heading")}
          </p>
          <ul className="mt-2 space-y-2.5">
            {RECOGNIZED.map((r, i) => (
              <li key={i} className="flex items-center gap-2">
                <span
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                    r.tint,
                  )}
                >
                  {r.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-900">
                    {t(r.nameKey)}
                  </p>
                  <span className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700 ring-1 ring-rose-200">
                    <Icon name="Heart" size={9} aria-hidden="true" />
                    {t(r.valueKey)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">
            {t("brochure.impact.mockup.aiInsight.heading")}
          </p>
          <p className="mt-1.5 text-xs leading-snug text-rose-900/80">
            {t("brochure.impact.mockup.aiInsight.body")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const ImpactBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "Gauge",
      title: t("brochure.impact.features.eNps.title"),
      description: t("brochure.impact.features.eNps.description"),
    },
    {
      icon: "Heart",
      title: t("brochure.impact.features.recognition.title"),
      description: t("brochure.impact.features.recognition.description"),
    },
    {
      icon: "Target",
      title: t("brochure.impact.features.goals.title"),
      description: t("brochure.impact.features.goals.description"),
    },
    {
      icon: "MessageSquare",
      title: t("brochure.impact.features.reflections.title"),
      description: t("brochure.impact.features.reflections.description"),
    },
    {
      icon: "Sparkles",
      title: t("brochure.impact.features.ai.title"),
      description: t("brochure.impact.features.ai.description"),
    },
    {
      icon: "FileText",
      title: t("brochure.impact.features.snapshots.title"),
      description: t("brochure.impact.features.snapshots.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: "+18",
      label: t("brochure.impact.metrics.npsLabel"),
    },
    {
      value: "3.2×",
      label: t("brochure.impact.metrics.recognitionLabel"),
    },
    {
      value: t("brochure.impact.metrics.packetValue"),
      label: t("brochure.impact.metrics.packetLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.impact.useCases.lead.persona"),
      scenario: t("brochure.impact.useCases.lead.scenario"),
      outcome: t("brochure.impact.useCases.lead.outcome"),
    },
    {
      persona: t("brochure.impact.useCases.manager.persona"),
      scenario: t("brochure.impact.useCases.manager.scenario"),
      outcome: t("brochure.impact.useCases.manager.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="impact"
      hero={{
        eyebrow: t("brochure.impact.hero.eyebrow"),
        headline: t("brochure.impact.hero.headline"),
        subheadline: t("brochure.impact.hero.subheadline"),
        illustration: <ImpactIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.impact.mockup.caption"),
        content: <ImpactMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default ImpactBrochure;
